// editor-data-fx.tsx - Data (items, quests, dialogue) and FX (materials, particles)
// Ported from the design source (editor-data-fx.jsx).
// Persists through the mode-document store as kinds 'gamedata' and 'fx'
// (see GameDataDoc, FxDoc).
// Exports: GameDataListPanel, GameDataEditorPanel, FxMaterialListPanel,
//          FxMaterialPreviewPanel, FxMaterialPropertiesPanel, FxParticlesLegacyRoute
import type { ThreeEvent } from '@react-three/fiber';
import { Environment, OrbitControls, PerspectiveCamera, useTexture } from '@react-three/drei';
import { Component, Fragment, Suspense, useEffect, useRef, useState, type ReactNode } from 'react';
import type { Texture } from 'three';
import {
  FX_EMITTER_PRESETS, generateNormalMapFromAlbedo, generateTexture, ParticleEmitter,
  TEXTURE_GENERATOR_KINDS, Viewport, type FxEmitterPreset, type TextureGeneratorKind,
} from '../../engine';
import { loadImagePixels, pixelsToPngFile } from '../assets/pixelCodec';
import { uploadTextureAsset } from '../assets/textureUpload';
import { useEditorHost } from './EditorHost';
import { M2Label, M2Panel } from './editor-modes2';
import { M2ListRow, EdSlider } from './panelPrimitives';
import { TabStrip, type TabDescriptor } from '../../engine/editor/components/Tabs';
import { EdConfirmDialog } from './editor-panels';
import { useProject } from '../project/ProjectProvider';
import { useSharedModeDocument } from './panel-system/sharedModeDocument';
import { createTransientSelectionStore } from './panel-system/transientSelection';
import { discardFxMaterialPaintSession, PAINT_CANVAS_SIZE, useFxMaterialPaintSession } from './fxMaterialPaintSession';

/* ── Data: game databases ─────────────────────────────────────────────── */
export interface DataItem {
  name: string;
  type: string;
  stack: number;
  value: number;
}

export interface DataQuest {
  name: string;
  steps: string[];
  reward: string;
}

export interface DataDialogueNode {
  speaker: string;
  text: string;
  leads: string;
}

export const DATA_ITEMS: DataItem[] = [
  { name: 'relic_key', type: 'Key item', stack: 1, value: 0 },
  { name: 'med_pack', type: 'Consumable', stack: 4, value: 50 },
  { name: 'smoke_x2', type: 'Utility', stack: 2, value: 30 },
  { name: 'lantern_oil', type: 'Resource', stack: 12, value: 8 },
];
export const DATA_QUESTS: DataQuest[] = [
  { name: 'Light the beacon', steps: ['Find lantern oil', 'Climb the lighthouse', 'Ignite the beacon'], reward: 'relic_key' },
  { name: 'Hold the gate', steps: ['Reach the gate', 'Survive 90 seconds'], reward: 'med_pack x2' },
];
export const DATA_DIALOGUE: DataDialogueNode[] = [
  { speaker: 'KEEPER', text: 'The lighthouse remembers everyone who climbs it.', leads: 'choice' },
  { speaker: 'YOU', text: 'Then it knows why I am here.', leads: 'keeper_02' },
  { speaker: 'YOU', text: 'I only need the key.', leads: 'keeper_03' },
];

export type DataTab = 'Items' | 'Quests' | 'Dialogue';

/** The 'gamedata' mode document: the items, quests and dialogue databases. */
export type GameDataDoc = {
  items: DataItem[];
  quests: DataQuest[];
  dialogue: DataDialogueNode[];
};

export const GAMEDATA_DOC_FALLBACK: GameDataDoc = { items: DATA_ITEMS, quests: DATA_QUESTS, dialogue: DATA_DIALOGUE };

// Shared across the two panels below (docs/editor-consolidation/
// panel-content-spec.md §8): which database tab and which row within it.
// Both set by data.list; both read by data.editor to know which record and
// which form shape to render.
export const useDataActiveTab = createTransientSelectionStore<DataTab>('Items');
export const useDataSelectedIndex = createTransientSelectionStore<number>(0);

const DATA_TABS: TabDescriptor[] = [
  { id: 'Items', label: 'Items' },
  { id: 'Quests', label: 'Quests' },
  { id: 'Dialogue', label: 'Dialogue' },
];

/** Tab switcher, row list, "+ New", delete (docs/editor-consolidation/
 * panel-content-spec.md §8, `data.list`). `pendingDelete` stays local - only
 * this panel triggers and renders the confirmation. */
export function GameDataListPanel() {
  const { data: storedDoc, setData: saveDoc } = useSharedModeDocument<GameDataDoc>('gamedata', GAMEDATA_DOC_FALLBACK);
  const [tab, setTab] = useDataActiveTab.use();
  const [sel, setSel] = useDataSelectedIndex.use();
  const { items, quests, dialogue } = storedDoc;

  const lists: Record<DataTab, string[]> = { Items: items.map((i) => i.name), Quests: quests.map((q) => q.name), Dialogue: dialogue.map((d) => d.speaker + ': ' + d.text.slice(0, 22) + '…') };
  const currentList = tab === 'Items' ? items : tab === 'Quests' ? quests : dialogue;

  function uniqueName(base: string, taken: string[]): string {
    if (!taken.includes(base)) return base;
    let n = 2;
    while (taken.includes(base + '_' + n)) n += 1;
    return base + '_' + n;
  }
  function addEntry() {
    if (tab === 'Items') {
      saveDoc((doc) => ({ ...doc, items: [...doc.items, { name: uniqueName('new_item', doc.items.map((x) => x.name)), type: 'Consumable', stack: 1, value: 0 }] }));
      setSel(items.length);
    } else if (tab === 'Quests') {
      saveDoc((doc) => ({ ...doc, quests: [...doc.quests, { name: uniqueName('New quest', doc.quests.map((x) => x.name)), steps: ['First objective'], reward: '' }] }));
      setSel(quests.length);
    } else {
      saveDoc((doc) => ({ ...doc, dialogue: [...doc.dialogue, { speaker: 'NPC', text: 'New line…', leads: '' }] }));
      setSel(dialogue.length);
    }
  }
  const [pendingDelete, setPendingDelete] = useState<number | null>(null);
  function deleteEntry(index: number) {
    if (tab === 'Items') saveDoc((doc) => ({ ...doc, items: doc.items.filter((_, i) => i !== index) }));
    else if (tab === 'Quests') saveDoc((doc) => ({ ...doc, quests: doc.quests.filter((_, i) => i !== index) }));
    else saveDoc((doc) => ({ ...doc, dialogue: doc.dialogue.filter((_, i) => i !== index) }));
    setSel((s) => Math.max(0, s >= index ? s - 1 : s));
  }

  return (
    <M2Panel side="left" width={252}>
      <M2Label>Database</M2Label>
      <TabStrip
        listLabel="Database"
        variant="segmented"
        tabs={DATA_TABS}
        activeId={tab}
        onSelect={(id) => { setTab(id as DataTab); setSel(0); }}
        style={{ margin: '0 16px 12px' }}
      />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, padding: '0 8px' }}>
        {lists[tab].map((name, i) => (
          <M2ListRow
            key={i}
            active={sel === i}
            onClick={() => setSel(i)}
            labelStyle={{ fontSize: 'var(--ss-fs-xs)' }}
            onDelete={currentList.length > 1 ? () => setPendingDelete(i) : undefined}
            deleteLabel={'Delete ' + name}
          >
            {name}
          </M2ListRow>
        ))}
        <button className="ss-btn ss-btn--ghost" onClick={addEntry} style={{ margin: '8px 4px 0', padding: '5px 0', fontSize: 'var(--ss-fs-xs)', borderRadius: 8, justifyContent: 'center' }}>+ New</button>
      </div>
      {pendingDelete !== null && (
        <EdConfirmDialog
          title={'Delete ' + (lists[tab][pendingDelete] ?? 'entry') + '?'}
          body={tab === 'Items'
            ? 'Logic rules that reference a removed item are disabled until they are repointed.'
            : 'This removes the entry from the ' + tab.toLowerCase() + ' database.'}
          confirmLabel="Delete"
          onConfirm={() => { deleteEntry(pendingDelete); setPendingDelete(null); }}
          onCancel={() => setPendingDelete(null)}
        />
      )}
    </M2Panel>
  );
}

/** Detail form, shape switched by the shared active tab (docs/editor-
 * consolidation/panel-content-spec.md §8, `data.editor`). */
export function GameDataEditorPanel() {
  const { data: storedDoc, setData: saveDoc } = useSharedModeDocument<GameDataDoc>('gamedata', GAMEDATA_DOC_FALLBACK);
  const [tab] = useDataActiveTab.use();
  const [sel] = useDataSelectedIndex.use();
  const { items, quests, dialogue } = storedDoc;

  const patchItem = (patch: Partial<DataItem>) => saveDoc((doc) => ({ ...doc, items: doc.items.map((x, i) => (i === sel ? { ...x, ...patch } : x)) }));
  const patchQuest = (patch: Partial<DataQuest>) => saveDoc((doc) => ({ ...doc, quests: doc.quests.map((x, i) => (i === sel ? { ...x, ...patch } : x)) }));
  const patchDialogue = (patch: Partial<DataDialogueNode>) => saveDoc((doc) => ({ ...doc, dialogue: doc.dialogue.map((x, i) => (i === sel ? { ...x, ...patch } : x)) }));

  return (
    <div style={{ flex: 1, minWidth: 0, overflowY: 'auto' }}>
      <div style={{ maxWidth: 560, margin: '0 auto', padding: '36px 32px 64px', display: 'flex', flexDirection: 'column', gap: 14 }}>
        {tab === 'Items' && items.length > 0 && (() => { const it = items[sel] || items[0]!; return (
          <div className="set-card" key={sel}>
            <div className="ss-field"><span className="ss-field-label">Item id</span>
              <input className="ss-input" value={it.name} onChange={(e) => patchItem({ name: e.target.value })} style={{ padding: '8px 11px', fontSize: 'var(--ss-fs-sm)', fontWeight: 600 }} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="ss-field"><span className="ss-field-label">Type</span>
                <select className="ss-input" value={it.type} onChange={(e) => patchItem({ type: e.target.value })} style={{ appearance: 'none', padding: '8px 11px', fontSize: 'var(--ss-fs-sm)' }}>
                  <option>Key item</option><option>Consumable</option><option>Utility</option><option>Resource</option><option>Equipment</option>
                </select>
              </div>
              <div className="ss-field"><span className="ss-field-label">Max stack</span>
                <input className="ss-input" value={it.stack} onChange={(e) => { const v = Number(e.target.value); if (Number.isFinite(v)) patchItem({ stack: v }); }} style={{ padding: '8px 11px', fontSize: 'var(--ss-fs-sm)' }} />
              </div>
            </div>
            <span className="hint">Items appear in the HUD hotbar and as Logic conditions (Has item) automatically.</span>
          </div>
        ); })()}
        {tab === 'Quests' && quests.length > 0 && (() => { const q = quests[sel] || quests[0]!; return (
          <div className="set-card" key={sel}>
            <div className="ss-field"><span className="ss-field-label">Quest name</span>
              <input className="ss-input" value={q.name} onChange={(e) => patchQuest({ name: e.target.value })} style={{ padding: '8px 11px', fontSize: 'var(--ss-fs-sm)', fontWeight: 600 }} />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {q.steps.map((s, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 'var(--ss-fs-sm)', color: 'var(--ss-text-2)' }}>
                  <span style={{ width: 20, height: 20, borderRadius: '50%', border: '1px solid var(--ss-border)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 'var(--ss-fs-2xs)', color: 'var(--ss-accent)', flexShrink: 0 }}>{i + 1}</span>
                  <input className="ss-input" value={s} onChange={(e) => patchQuest({ steps: q.steps.map((x, j) => (j === i ? e.target.value : x)) })} style={{ flex: 1, padding: '6px 10px', fontSize: 'var(--ss-fs-sm)' }} />
                  {q.steps.length > 1 && (
                    <button onClick={() => patchQuest({ steps: q.steps.filter((_, j) => j !== i) })} title="Remove objective" aria-label={'Remove objective ' + (i + 1)}
                      style={{ border: 'none', background: 'transparent', padding: '2px 6px', fontSize: 'var(--ss-fs-sm)', color: 'var(--ss-text-3)', flexShrink: 0 }}>×</button>
                  )}
                </div>
              ))}
              <button className="ss-btn ss-btn--ghost ss-btn--sm" onClick={() => patchQuest({ steps: [...q.steps, 'New objective'] })} style={{ alignSelf: 'flex-start' }}>+ Objective</button>
            </div>
            <div className="ss-field"><span className="ss-field-label">Reward</span>
              <input className="ss-input" value={q.reward} onChange={(e) => patchQuest({ reward: e.target.value })} style={{ padding: '8px 11px', fontSize: 'var(--ss-fs-sm)' }} />
            </div>
            <span className="hint">Objectives and rewards are reference text for now; Logic rules and the HUD don't read quest state yet.</span>
          </div>
        ); })()}
        {tab === 'Dialogue' && dialogue.length > 0 && (() => { const d = dialogue[sel] || dialogue[0]!; return (
          <div className="set-card" key={sel}>
            <h3>Dialogue node</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: 12 }}>
              <div className="ss-field"><span className="ss-field-label">Speaker</span>
                <input className="ss-input" value={d.speaker} onChange={(e) => patchDialogue({ speaker: e.target.value })} style={{ padding: '8px 11px', fontSize: 'var(--ss-fs-sm)' }} />
              </div>
              <div className="ss-field"><span className="ss-field-label">Leads to</span>
                <input className="ss-input" value={d.leads} onChange={(e) => patchDialogue({ leads: e.target.value })} style={{ padding: '8px 11px', fontSize: 'var(--ss-fs-sm)' }} />
              </div>
            </div>
            <div className="ss-field"><span className="ss-field-label">Line</span>
              <textarea className="ss-input" rows={3} value={d.text} onChange={(e) => patchDialogue({ text: e.target.value })} style={{ resize: 'vertical', fontFamily: 'inherit', fontSize: 'var(--ss-fs-sm)' }} />
            </div>
            <span className="hint">Player choices branch the tree via Leads to. Logic and the HUD don't read dialogue state yet.</span>
          </div>
        ); })()}
      </div>
    </div>
  );
}

/* ── FX: materials and particles ──────────────────────────────────────── */

/** Generates a stable id for a newly-created `FxMaterial`, independent of
 * its (mutable, renameable) `name` - same fallback pattern usePeerCursors.ts
 * uses for its own random client id. */
function randomFxMaterialId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  return `fxmat-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

/** A material preset: applied to instances from the Edit inspector. Surface
 * values are 0..1 (engine material domain); sliders show percentages. */
export interface FxMaterial {
  /** Stable identity, generated once and never changed by a rename -
   * everything that needs to keep referring to THIS material across a
   * rename (an in-flight texture upload's completion handler, chiefly)
   * keys off this, not `name`. Older saved documents persisted before this
   * field existed have one backfilled on load by `migrateFxDoc` (derived
   * deterministically from the material's own name at that point, so it
   * stays stable across repeated migrations of the same not-yet-resaved
   * document, and becomes a real persisted field the next time anything
   * about the document saves). */
  id: string;
  name: string;
  colour: string;
  roughness: number;
  metalness: number;
  emissive: number;
  /** Uploaded texture map asset URLs (docs/3d-content-pipeline/roadmap.md
   * 3.2 Phase 1). Undefined means untextured - the same "leave it alone"
   * convention gltfMaterialOverrides already applies to scalar fields. */
  albedoMap?: string;
  normalMap?: string;
  roughnessMap?: string;
  metalnessMap?: string;
}

/** A particle preset backing the placeable fx_emitter object. */
export interface FxParticle {
  name: string;
  preset: FxEmitterPreset;
  rate: number;
  size: number;
  colour: string;
}

export const FX_MATERIAL_PRESETS: FxMaterial[] = [
  { id: 'preset-greybox_standard', name: 'greybox_standard', colour: '#6e7480', roughness: 0.8, metalness: 0.05, emissive: 0 },
  { id: 'preset-metal_worn', name: 'metal_worn', colour: '#9aa3ad', roughness: 0.45, metalness: 0.85, emissive: 0 },
  { id: 'preset-concrete_raw', name: 'concrete_raw', colour: '#8b9099', roughness: 0.95, metalness: 0.02, emissive: 0 },
  { id: 'preset-glass_tint', name: 'glass_tint', colour: '#71b4fe', roughness: 0.15, metalness: 0.1, emissive: 0 },
  { id: 'preset-emissive_strip', name: 'emissive_strip', colour: '#71b4fe', roughness: 0.4, metalness: 0.1, emissive: 0.8 },
];

export const FX_PARTICLE_PRESETS: FxParticle[] = [
  { name: 'spark_burst', preset: 'spark_burst', rate: 40, size: 36, colour: '#71b4fe' },
  { name: 'smoke_soft', preset: 'smoke_soft', rate: 24, size: 60, colour: '#a8b0d2' },
  { name: 'ember_drift', preset: 'ember_drift', rate: 32, size: 28, colour: '#e8a06c' },
  { name: 'pickup_glow', preset: 'pickup_glow', rate: 16, size: 44, colour: '#988af6' },
];

/** The 'fx' mode document: the editable material and particle preset lists. */
export type FxDoc = {
  materials: FxMaterial[];
  particles: FxParticle[];
};

export const FX_DOC_FALLBACK: FxDoc = { materials: FX_MATERIAL_PRESETS, particles: FX_PARTICLE_PRESETS };

/**
 * Normalise any stored fx document, including the retired name-list shape
 * ({ materials: string[], particles: string[], rough, metal, rate }).
 */
export function migrateFxDoc(raw: unknown): FxDoc {
  const doc = (raw ?? {}) as { materials?: unknown; particles?: unknown; rough?: unknown; metal?: unknown; rate?: unknown };
  const oldRough = typeof doc.rough === 'number' ? doc.rough / 100 : 0.68;
  const oldMetal = typeof doc.metal === 'number' ? doc.metal / 100 : 0.12;
  const oldRate = typeof doc.rate === 'number' ? doc.rate : 40;

  const materials: FxMaterial[] = Array.isArray(doc.materials)
    ? doc.materials.flatMap((m) => {
        if (typeof m === 'string') {
          const preset = FX_MATERIAL_PRESETS.find((p) => p.name === m);
          // A bare-name legacy entry with no matching preset has no id of
          // its own to persist - backfill one deterministically from its
          // name (the entry's own de facto primary key up to this point),
          // so repeated migrations of the same not-yet-resaved document
          // keep producing the SAME id rather than a fresh random one every
          // time this pure function runs (every render, in several
          // callers) until something finally saves it back explicitly.
          return [preset ?? { id: m, name: m, colour: '#6e7480', roughness: oldRough, metalness: oldMetal, emissive: 0 }];
        }
        const mat = m as Partial<FxMaterial>;
        if (typeof mat.name !== 'string') return [];
        return [{
          // Same deterministic backfill as above for a document saved after
          // FxMaterial gained real fields but before it gained `id`.
          id: typeof mat.id === 'string' && mat.id.length > 0 ? mat.id : mat.name,
          name: mat.name,
          colour: typeof mat.colour === 'string' ? mat.colour : '#6e7480',
          roughness: typeof mat.roughness === 'number' ? mat.roughness : 0.8,
          metalness: typeof mat.metalness === 'number' ? mat.metalness : 0.05,
          emissive: typeof mat.emissive === 'number' ? mat.emissive : 0,
          albedoMap: typeof mat.albedoMap === 'string' ? mat.albedoMap : undefined,
          normalMap: typeof mat.normalMap === 'string' ? mat.normalMap : undefined,
          roughnessMap: typeof mat.roughnessMap === 'string' ? mat.roughnessMap : undefined,
          metalnessMap: typeof mat.metalnessMap === 'string' ? mat.metalnessMap : undefined,
        }];
      })
    : [];
  const particles: FxParticle[] = Array.isArray(doc.particles)
    ? doc.particles.flatMap((p) => {
        if (typeof p === 'string') {
          const preset = FX_PARTICLE_PRESETS.find((x) => x.name === p);
          return [preset ? { ...preset, rate: oldRate } : { name: p, preset: 'spark_burst' as const, rate: oldRate, size: 36, colour: '#71b4fe' }];
        }
        const part = p as Partial<FxParticle>;
        if (typeof part.name !== 'string') return [];
        return [{
          name: part.name,
          preset: FX_EMITTER_PRESETS.includes(part.preset as FxEmitterPreset) ? (part.preset as FxEmitterPreset) : 'spark_burst',
          rate: typeof part.rate === 'number' ? part.rate : 40,
          size: typeof part.size === 'number' ? part.size : 36,
          colour: typeof part.colour === 'string' ? part.colour : '#71b4fe',
        }];
      })
    : [];
  return {
    materials: materials.length > 0 ? materials : FX_MATERIAL_PRESETS,
    particles: particles.length > 0 ? particles : FX_PARTICLE_PRESETS,
  };
}

/**
 * Patches the material identified by `id` (not `name`) - exported (and
 * framework-free) so the fix for the texture-upload/rename race is directly
 * unit-testable without mounting the full properties panel. Renaming a
 * material no longer changes its `id`, so a completion handler that
 * captured `id` at upload-start time (see `FxMaterialPropertiesPanel`'s
 * `uploadMap`/`generateAndApply`/`deriveNormalMap`) keeps finding the right
 * record here even if the material was renamed while the upload was still
 * in flight.
 */
export function patchFxMaterialById(materials: FxMaterial[], id: string, changes: Partial<FxMaterial>): FxMaterial[] {
  return materials.map((m) => (m.id === id ? { ...m, ...changes } : m));
}

/**
 * Renames the material identified by `id` (not its own current `name`) to
 * `rawName` (trimmed) - a no-op if the id doesn't exist, the trimmed name is
 * empty, it matches the material's current name already, or it collides
 * with a DIFFERENT material's current name. Exported for the same
 * direct-unit-testability reason as `patchFxMaterialById`.
 */
export function renameFxMaterial(materials: FxMaterial[], id: string, rawName: string): FxMaterial[] {
  const nextName = rawName.trim();
  const current = materials.find((m) => m.id === id);
  if (!current || !nextName || nextName === current.name) return materials;
  if (materials.some((m) => m.id !== id && m.name === nextName)) return materials;
  return materials.map((m) => (m.id === id ? { ...m, name: nextName } : m));
}

const ALBEDO_SWATCHES = ['#6e7480', '#3c3c6f', '#2142d4', '#71b4fe', '#d8c08a', '#c75c2e'];
/** Adds black/white to the brush palette without touching `ALBEDO_SWATCHES`
 * itself (still used bare for the material's own colour swatches) - hoisted
 * here rather than `.concat()`-ed at render time so it isn't a fresh array
 * on every render. */
const BRUSH_SWATCHES = [...ALBEDO_SWATCHES, '#000000', '#ffffff'];

/** Display labels for the procedural-generator kind picker. */
const GENERATOR_LABELS: Record<TextureGeneratorKind, string> = {
  noise: 'Noise', gradient: 'Gradient', checker: 'Checker', stripe: 'Stripe', brick: 'Brick',
};

const GENERATED_TEXTURE_SIZE = 256;

// Shared across the three materials panels below (docs/editor-consolidation/
// panel-content-spec.md §9). Materials-scoped only - these panels carry no
// tab concept, unlike the old FxRoute.
export const useFxSelectedMaterialName = createTransientSelectionStore<string>('greybox_standard');

/** Materials-only rows, "+ New", rename, delete (docs/editor-consolidation/
 * panel-content-spec.md §9, `fx.materials-list`) - drops the Materials/
 * Particles tab switcher entirely; Particles keeps its own legacy panel
 * (fx.particles-legacy) below. */
export function FxMaterialListPanel() {
  const { data: storedDoc, setData: saveDoc } = useSharedModeDocument<FxDoc>('fx', FX_DOC_FALLBACK);
  const [sel, setSel] = useFxSelectedMaterialName.use();
  const mats = migrateFxDoc(storedDoc).materials;
  const { projectId } = useEditorHost();
  // Keyed the same way the Preview/Properties panels key their own paint
  // session, so switching the selected material here tears down (or keeps
  // alive) the exact same session those panels observe.
  const currentPaintSession = useFxMaterialPaintSession(projectId, mats.find((m) => m.name === sel)?.id ?? '');
  const [pendingSwitchTo, setPendingSwitchTo] = useState<string | null>(null);

  function addEntry() {
    const name = `material_${mats.length + 1}`;
    const id = randomFxMaterialId();
    saveDoc((doc) => {
      const migrated = migrateFxDoc(doc);
      return { ...migrated, materials: [...migrated.materials, { id, name, colour: '#6e7480', roughness: 0.8, metalness: 0.05, emissive: 0 }] };
    });
    setSel(name);
  }
  function selectMaterial(name: string) {
    if (name === sel) return;
    // A dirty paint session only ever exists on the currently-selected
    // material (fxMaterialPaintSession.ts keys by material id, and Paint
    // mode always targets the active selection) - switching away discards
    // it, so gate that behind confirmation rather than losing work silently.
    if (currentPaintSession.dirty) {
      setPendingSwitchTo(name);
      return;
    }
    setSel(name);
  }
  function confirmSwitch() {
    if (pendingSwitchTo) setSel(pendingSwitchTo);
    setPendingSwitchTo(null);
  }
  const [pendingDelete, setPendingDelete] = useState<string | null>(null);
  function confirmDeleteEntry() {
    const name = pendingDelete;
    if (!name) return;
    const deletedId = mats.find((m) => m.name === name)?.id;
    setPendingDelete(null);
    saveDoc((doc) => {
      const migrated = migrateFxDoc(doc);
      return { ...migrated, materials: migrated.materials.filter((m) => m.name !== name) };
    });
    // A deleted material's own paint session (if any) has nothing left to
    // resume painting for - without this, a material deleted mid-paint
    // would leak its canvas/texture forever, since release() deliberately
    // keeps a dirty session alive for exactly this kind of incidental
    // unmount (see fxMaterialPaintSession.ts's own comments on both).
    if (deletedId) discardFxMaterialPaintSession(projectId, deletedId);
    if (sel === name) setSel(mats.find((m) => m.name !== name)?.name ?? '');
  }

  return (
    <M2Panel side="left" width={244}>
      <M2Label>Materials</M2Label>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, padding: '0 8px' }}>
        {mats.map((m) => (
          <M2ListRow
            key={m.name}
            active={sel === m.name}
            onClick={() => selectMaterial(m.name)}
            onDelete={mats.length > 1 ? () => setPendingDelete(m.name) : undefined}
            deleteLabel={'Delete ' + m.name}
          >
            {m.name}
          </M2ListRow>
        ))}
        <button className="ss-btn ss-btn--ghost" onClick={addEntry} style={{ margin: '8px 4px 0', padding: '5px 0', fontSize: 'var(--ss-fs-xs)', borderRadius: 8, justifyContent: 'center' }}>+ New</button>
      </div>
      {pendingSwitchTo && (
        <EdConfirmDialog
          title="Discard unsaved paint strokes?"
          body={`Switching materials discards what you've painted on "${sel}" since it was last saved as an albedo map.`}
          confirmLabel="Discard and switch"
          onConfirm={confirmSwitch}
          onCancel={() => setPendingSwitchTo(null)}
        />
      )}
      {pendingDelete && (
        <EdConfirmDialog
          title={`Delete "${pendingDelete}"?`}
          body="Objects already using this material in Edit mode keep their current look, but its name disappears from the Material dropdown, so it can no longer be selected or re-applied."
          confirmLabel="Delete"
          onConfirm={confirmDeleteEntry}
          onCancel={() => setPendingDelete(null)}
        />
      )}
    </M2Panel>
  );
}

/** Untextured fallback sphere: the Suspense/error-boundary fallback below
 * while a map is loading, or in place of one that failed to load, and the
 * whole preview whenever no material has any map set. */
function UntexturedPreviewSphere({ material }: { material: FxMaterial }) {
  return (
    <mesh>
      <sphereGeometry args={[0.95, 48, 32]} />
      <meshStandardMaterial
        color={material.colour}
        roughness={material.roughness}
        metalness={material.metalness}
        emissive={material.emissive > 0 ? material.colour : '#000000'}
        emissiveIntensity={material.emissive}
      />
    </mesh>
  );
}

/** The real preview sphere: loads whichever maps the material has set (drei's
 * `useTexture` accepts a partial map record and suspends until loaded) and
 * an IBL environment for reflections, so this is a true render instead of a
 * faked one.
 *
 * `paintTexture`, when set (Paint mode), stands in for the material's own
 * `albedoMap` - a live `CanvasTexture` owned by fxMaterialPaintSession.ts,
 * updated in place by every brush stamp - and the other three maps are left
 * off entirely rather than loaded, since painting only ever touches albedo
 * (docs/3d-content-pipeline/roadmap.md 3.2 Phase 3, v1 scope). Passing an
 * empty `urls` record to `useTexture` in that case is a real, safe no-op
 * (nothing to fetch), not a conditional hook call. The three optional
 * pointer handlers are wired onto the mesh only while a caller wants
 * Paint-mode interaction; View mode passes none, leaving OrbitControls the
 * only thing listening. */
function MaterialPreviewSphere({ material, paintTexture, onPointerDown, onPointerMove, onPointerUp }: {
  material: FxMaterial;
  paintTexture?: Texture | null;
  onPointerDown?: (e: ThreeEvent<PointerEvent>) => void;
  onPointerMove?: (e: ThreeEvent<PointerEvent>) => void;
  onPointerUp?: (e: ThreeEvent<PointerEvent>) => void;
}) {
  const urls: Record<string, string> = {};
  if (!paintTexture) {
    if (material.albedoMap) urls.map = material.albedoMap;
    if (material.normalMap) urls.normalMap = material.normalMap;
    if (material.roughnessMap) urls.roughnessMap = material.roughnessMap;
    if (material.metalnessMap) urls.metalnessMap = material.metalnessMap;
  }
  const textures = useTexture(urls);
  return (
    <Fragment>
      <Environment preset="city" />
      <mesh onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp}>
        <sphereGeometry args={[0.95, 48, 32]} />
        <meshStandardMaterial
          color={material.colour}
          roughness={material.roughness}
          metalness={material.metalness}
          emissive={material.emissive > 0 ? material.colour : '#000000'}
          emissiveIntensity={material.emissive}
          map={paintTexture ?? textures.map ?? null}
          normalMap={paintTexture ? null : textures.normalMap ?? null}
          roughnessMap={paintTexture ? null : textures.roughnessMap ?? null}
          metalnessMap={paintTexture ? null : textures.metalnessMap ?? null}
        />
      </mesh>
    </Fragment>
  );
}

/**
 * Isolates the preview's async loads (the IBL environment, any texture map)
 * so a failed fetch - a deleted asset, a network hiccup - degrades to
 * `fallback` instead of blanking the whole panel, the same boundary-per-
 * async-load discipline MapObjectErrorBoundary already applies to placed
 * glTF models.
 */
interface MaterialPreviewErrorBoundaryProps {
  resetKey: string;
  fallback: ReactNode;
  children: ReactNode;
}
interface MaterialPreviewErrorBoundaryState {
  error: boolean;
}
class MaterialPreviewErrorBoundary extends Component<MaterialPreviewErrorBoundaryProps, MaterialPreviewErrorBoundaryState> {
  state: MaterialPreviewErrorBoundaryState = { error: false };
  static getDerivedStateFromError(): MaterialPreviewErrorBoundaryState {
    return { error: true };
  }
  componentDidCatch(error: Error) {
    console.error('[fx-materials] preview failed to load:', error);
  }
  componentDidUpdate(prevProps: MaterialPreviewErrorBoundaryProps) {
    if (prevProps.resetKey !== this.props.resetKey && this.state.error) this.setState({ error: false });
  }
  render(): ReactNode {
    return this.state.error ? this.props.fallback : this.props.children;
  }
}

/** Real WebGL preview for the selected material (docs/editor-consolidation/
 * panel-content-spec.md §9, `fx.materials-preview`) - a textured
 * meshStandardMaterial sphere under an IBL environment, replacing the old
 * CSS-gradient mockup.
 *
 * Also owns the Paint-mode viewport interaction (docs/3d-content-pipeline/
 * roadmap.md 3.2 Phase 3): the View/Paint toggle, seeding a fresh paint
 * session the first time Paint mode is entered for a material (from its
 * existing albedo map if it has one, otherwise a flat fill of its base
 * colour - see fxMaterialPaintSession.ts's `seedSolid` doc comment for why
 * that seed must be opaque), and the pointer handlers that turn a raycast
 * hit's UV into brush strokes. `OrbitControls` is disabled while painting -
 * it attaches its own native DOM listeners straight to the canvas, entirely
 * outside React's synthetic event tree, so nothing short of `enabled={false}`
 * actually stops it from also rotating the camera under a paint drag. */
export function FxMaterialPreviewPanel() {
  const { data: storedDoc } = useSharedModeDocument<FxDoc>('fx', FX_DOC_FALLBACK);
  const [sel] = useFxSelectedMaterialName.use();
  const mats = migrateFxDoc(storedDoc).materials;
  const mat = mats.find((m) => m.name === sel) ?? mats[0];
  const { projectId } = useEditorHost();
  const session = useFxMaterialPaintSession(projectId, mat?.id ?? '');
  const [confirmLeavePaint, setConfirmLeavePaint] = useState(false);
  const resetKey = mat
    ? [mat.name, mat.albedoMap, mat.normalMap, mat.roughnessMap, mat.metalnessMap].map((x) => x ?? '').join('|')
    : '';

  async function enterPaintMode() {
    if (!mat) return;
    session.setMode('paint');
    if (session.seeded) return;
    if (mat.albedoMap) {
      try {
        const { pixels, width, height } = await loadImagePixels(mat.albedoMap);
        session.seedFrom(pixels, width, height);
        return;
      } catch {
        // Falls through to the flat-colour seed below - a deleted asset or
        // a network hiccup should not block entering Paint mode.
      }
    }
    session.seedSolid(mat.colour);
  }
  function requestViewMode() {
    if (session.dirty) {
      setConfirmLeavePaint(true);
      return;
    }
    session.setMode('view');
  }
  function confirmLeavePaintMode() {
    session.setMode('view');
    setConfirmLeavePaint(false);
  }

  function handlePaintDown(e: ThreeEvent<PointerEvent>) {
    if (!e.uv) return;
    e.stopPropagation();
    (e.target as Element | null)?.setPointerCapture?.(e.pointerId);
    session.beginStroke(e.uv.x, e.uv.y);
  }
  function handlePaintMove(e: ThreeEvent<PointerEvent>) {
    if (!e.uv) return;
    e.stopPropagation();
    session.strokeTo(e.uv.x, e.uv.y);
  }
  function handlePaintUp(e: ThreeEvent<PointerEvent>) {
    e.stopPropagation();
    (e.target as Element | null)?.releasePointerCapture?.(e.pointerId);
    session.endStroke();
  }

  const painting = session.mode === 'paint';

  return (
    <div style={{ position: 'relative', flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'radial-gradient(ellipse 70% 60% at 50% 40%, var(--ss-surface), var(--ss-bg-2) 80%)' }}>
      {mat && (
        <div style={{ position: 'relative', width: 320, height: 320 }}>
          <Viewport gl={{ antialias: true }}>
            <PerspectiveCamera makeDefault position={[0, 0.3, 2.6]} fov={40} />
            <color attach="background" args={['#0a0a0c']} />
            <ambientLight intensity={0.5} />
            <directionalLight position={[3, 4, 2]} intensity={1.2} />
            <Suspense fallback={<UntexturedPreviewSphere material={mat} />}>
              <MaterialPreviewErrorBoundary resetKey={resetKey} fallback={<UntexturedPreviewSphere material={mat} />}>
                <MaterialPreviewSphere
                  key={resetKey}
                  material={mat}
                  paintTexture={painting ? session.texture : null}
                  onPointerDown={painting ? handlePaintDown : undefined}
                  onPointerMove={painting ? handlePaintMove : undefined}
                  onPointerUp={painting ? handlePaintUp : undefined}
                />
              </MaterialPreviewErrorBoundary>
            </Suspense>
            <OrbitControls enabled={!painting} enablePan={false} minDistance={1.4} maxDistance={5} />
          </Viewport>
        </div>
      )}
      <span className="hm-chip" style={{ position: 'absolute', top: 14, left: 14, fontSize: 'var(--ss-fs-2xs)', padding: '7px 14px' }}>{sel}.material</span>
      {mat && (
        <div className="hm-chip" style={{ position: 'absolute', top: 14, right: 14, display: 'flex', padding: 3, gap: 2 }}>
          <button
            className={painting ? 'ss-btn ss-btn--ghost ss-btn--sm' : 'ss-btn ss-btn--primary ss-btn--sm'}
            onClick={requestViewMode}
          >
            View
          </button>
          <button
            className={painting ? 'ss-btn ss-btn--primary ss-btn--sm' : 'ss-btn ss-btn--ghost ss-btn--sm'}
            onClick={() => void enterPaintMode()}
          >
            Paint
          </button>
        </div>
      )}
      {confirmLeavePaint && (
        <EdConfirmDialog
          title="Discard unsaved paint strokes?"
          body="Switching back to View discards what you've painted since it was last saved as an albedo map."
          confirmLabel="Discard and switch to View"
          onConfirm={confirmLeavePaintMode}
          onCancel={() => setConfirmLeavePaint(false)}
        />
      )}
    </div>
  );
}

type TextureMapKey = 'albedoMap' | 'normalMap' | 'roughnessMap' | 'metalnessMap';
const TEXTURE_MAP_SLOTS: { key: TextureMapKey; label: string }[] = [
  { key: 'albedoMap', label: 'Albedo' },
  { key: 'normalMap', label: 'Normal' },
  { key: 'roughnessMap', label: 'Roughness' },
  { key: 'metalnessMap', label: 'Metalness' },
];

/** One upload/replace/clear row for a single texture map slot. Reuses
 * editor-model.tsx's Import .glb pattern: a hidden file input triggered by a
 * button, rather than a raw visible input. */
function TextureMapRow({ label, url, busy, onUpload, onClear }: {
  label: string;
  url?: string;
  busy: boolean;
  onUpload: (file: File) => void;
  onClear: () => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '0 16px', marginBottom: 8 }}>
      <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/webp" style={{ display: 'none' }}
        onChange={(e) => { const file = e.target.files?.[0]; e.target.value = ''; if (file) onUpload(file); }} />
      <span style={{ flex: 1, minWidth: 0, fontSize: 'var(--ss-fs-xs)', color: 'var(--ss-text-2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
      <button className="ss-btn ss-btn--ghost ss-btn--sm" disabled={busy} onClick={() => inputRef.current?.click()}>
        {busy ? 'Uploading…' : url ? 'Replace' : 'Upload'}
      </button>
      {url && (
        <button onClick={onClear} title={'Clear ' + label} aria-label={'Clear ' + label}
          style={{ border: 'none', background: 'transparent', padding: '2px 4px', fontSize: 'var(--ss-fs-sm)', lineHeight: 1, color: 'var(--ss-text-3)', flexShrink: 0 }}>×</button>
      )}
    </div>
  );
}

/** Name field, Roughness/Metallic/Emissive sliders, Albedo swatches, texture
 * map uploads (docs/editor-consolidation/panel-content-spec.md §9,
 * `fx.materials-properties`). */
export function FxMaterialPropertiesPanel() {
  const { data: storedDoc, setData: saveDoc } = useSharedModeDocument<FxDoc>('fx', FX_DOC_FALLBACK);
  const [sel, setSel] = useFxSelectedMaterialName.use();
  const mats = migrateFxDoc(storedDoc).materials;
  const mat = mats.find((m) => m.name === sel) ?? mats[0];
  const { projectId } = useEditorHost();
  const { reload } = useProject();
  const session = useFxMaterialPaintSession(projectId, mat?.id ?? '');
  const [uploadingSlot, setUploadingSlot] = useState<TextureMapKey | null>(null);
  const [uploadError, setUploadError] = useState('');
  const [genKind, setGenKind] = useState<TextureGeneratorKind>('noise');
  const [genColourA, setGenColourA] = useState('#1a1c22');
  const [genColourB, setGenColourB] = useState('#8b9099');
  const [genScale, setGenScale] = useState(4);
  const [genContrast, setGenContrast] = useState(0);
  const [genSeed, setGenSeed] = useState(1);
  const [genBusy, setGenBusy] = useState<'texture' | 'normal' | null>(null);
  const [genError, setGenError] = useState('');
  const [paintBusy, setPaintBusy] = useState(false);
  const [paintError, setPaintError] = useState('');
  const [pendingClear, setPendingClear] = useState(false);

  // Nothing else in this editor guards against losing an in-memory paint
  // session on tab close/reload (the existing beforeunload guard in
  // screens-editor.tsx is Map-Builder-specific) - a real, easily-hit gap
  // otherwise, since a paint session is never autosaved mid-stroke.
  useEffect(() => {
    if (!session.dirty) return;
    function handler(e: BeforeUnloadEvent) {
      e.preventDefault();
      e.returnValue = '';
    }
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [session.dirty]);

  // Keyed on the material's stable `id`, not its (mutable) `name` - a
  // rename mid-upload must not orphan the upload's own completion handler
  // (see `uploadMap`/`generateAndApply`/`deriveNormalMap` below, all of
  // which capture `id` at the moment the upload starts, not `name`).
  function patchMat(id: string, changes: Partial<FxMaterial>) {
    saveDoc((doc) => {
      const migrated = migrateFxDoc(doc);
      return { ...migrated, materials: patchFxMaterialById(migrated.materials, id, changes) };
    });
  }
  function renameEntry(id: string, rawName: string) {
    const current = mats.find((m) => m.id === id);
    const nextName = rawName.trim();
    if (!current || !nextName || nextName === current.name) return;
    if (mats.some((m) => m.id !== id && m.name === nextName)) return;
    saveDoc((doc) => {
      const migrated = migrateFxDoc(doc);
      return { ...migrated, materials: renameFxMaterial(migrated.materials, id, rawName) };
    });
    setSel(nextName);
  }
  async function uploadMap(id: string, slot: TextureMapKey, file: File) {
    setUploadingSlot(slot);
    setUploadError('');
    try {
      const asset = await uploadTextureAsset(projectId, file);
      patchMat(id, { [slot]: asset.downloadUrl });
      await reload();
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Upload failed.');
    } finally {
      setUploadingSlot(null);
    }
  }
  async function generateAndApply(id: string) {
    setGenBusy('texture');
    setGenError('');
    try {
      const pixels = generateTexture(genKind, {
        width: GENERATED_TEXTURE_SIZE, height: GENERATED_TEXTURE_SIZE,
        scale: genScale, contrast: genContrast / 100, seed: genSeed,
        colourA: genColourA, colourB: genColourB,
      });
      const file = await pixelsToPngFile(pixels, GENERATED_TEXTURE_SIZE, GENERATED_TEXTURE_SIZE, `${genKind}.png`);
      const asset = await uploadTextureAsset(projectId, file);
      patchMat(id, { albedoMap: asset.downloadUrl });
      await reload();
    } catch (err) {
      setGenError(err instanceof Error ? err.message : 'Generation failed.');
    } finally {
      setGenBusy(null);
    }
  }
  async function deriveNormalMap(id: string, albedoUrl: string) {
    setGenBusy('normal');
    setGenError('');
    try {
      const { pixels, width, height } = await loadImagePixels(albedoUrl);
      const normals = generateNormalMapFromAlbedo(pixels, width, height);
      const file = await pixelsToPngFile(normals, width, height, 'normal.png');
      const asset = await uploadTextureAsset(projectId, file);
      patchMat(id, { normalMap: asset.downloadUrl });
      await reload();
    } catch (err) {
      setGenError(err instanceof Error ? err.message : 'Normal-map generation failed.');
    } finally {
      setGenBusy(null);
    }
  }
  /** Bakes the live paint canvas's dilated pixels (see fxMaterialPaintSession
   * .ts's `getDilatedPixels` doc comment) to a PNG and uploads it as the
   * material's albedo map - the exact same one-shot upload-then-patch shape
   * as `generateAndApply`/`deriveNormalMap` above, just fed from a painted
   * canvas instead of a generator or a Sobel filter. */
  async function saveAsAlbedo(id: string) {
    setPaintBusy(true);
    setPaintError('');
    try {
      const dilated = session.getDilatedPixels(4);
      const file = await pixelsToPngFile(dilated, PAINT_CANVAS_SIZE, PAINT_CANVAS_SIZE, 'painted-albedo.png');
      const asset = await uploadTextureAsset(projectId, file);
      patchMat(id, { albedoMap: asset.downloadUrl });
      session.markSaved();
      await reload();
    } catch (err) {
      setPaintError(err instanceof Error ? err.message : 'Save failed.');
    } finally {
      setPaintBusy(false);
    }
  }

  if (!mat) return <M2Panel side="right" width={252}><div /></M2Panel>;

  return (
    <M2Panel side="right" width={252}>
      <M2Label>Name</M2Label>
      <div style={{ padding: '0 16px', marginBottom: 8 }}>
        <input className="ss-input" key={mat.id} defaultValue={mat.name}
          onBlur={(e) => renameEntry(mat.id, e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
          style={{ padding: '7px 10px', fontSize: 'var(--ss-fs-sm)', fontWeight: 500, borderRadius: 8 }} />
      </div>
      <M2Label>Surface</M2Label>
      <EdSlider label="Roughness" value={Math.round(mat.roughness * 100)} onChange={(v) => patchMat(mat.id, { roughness: v / 100 })} showExactValue />
      <EdSlider label="Metallic" value={Math.round(mat.metalness * 100)} onChange={(v) => patchMat(mat.id, { metalness: v / 100 })} showExactValue />
      <EdSlider label="Emissive" value={Math.round(mat.emissive * 100)} onChange={(v) => patchMat(mat.id, { emissive: v / 100 })} showExactValue />
      <M2Label>Albedo</M2Label>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 6, padding: '0 16px', marginBottom: 8 }}>
        {ALBEDO_SWATCHES.map((c) => (
          <button key={c} title={c} onClick={() => patchMat(mat.id, { colour: c })} style={{
            aspectRatio: '1', borderRadius: 7, background: c, cursor: 'pointer',
            border: '2px solid ' + (mat.colour === c ? 'var(--ss-accent)' : 'rgba(255,255,255,0.12)'),
          }} />
        ))}
      </div>
      <M2Label>Texture maps</M2Label>
      {TEXTURE_MAP_SLOTS.map((slot) => (
        <TextureMapRow key={slot.key} label={slot.label} url={mat[slot.key]}
          busy={uploadingSlot === slot.key}
          onUpload={(file) => void uploadMap(mat.id, slot.key, file)}
          onClear={() => patchMat(mat.id, { [slot.key]: undefined })} />
      ))}
      {uploadError && <div style={{ padding: '0 16px 8px', fontSize: 'var(--ss-fs-xs)', color: 'var(--ss-danger)', lineHeight: 1.5 }}>{uploadError}</div>}
      <M2Label>Generate</M2Label>
      <div style={{ padding: '0 16px', marginBottom: 8 }}>
        <select className="ss-input" value={genKind} onChange={(e) => setGenKind(e.target.value as TextureGeneratorKind)}
          style={{ appearance: 'none', padding: '7px 10px', fontSize: 'var(--ss-fs-sm)', borderRadius: 8, marginBottom: 8 }}>
          {TEXTURE_GENERATOR_KINDS.map((k) => <option key={k} value={k}>{GENERATOR_LABELS[k]}</option>)}
        </select>
        <div style={{ display: 'flex', gap: 8, marginBottom: 4 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 'var(--ss-fs-xs)', color: 'var(--ss-text-3)' }}>
            <input type="color" value={genColourA} onChange={(e) => setGenColourA(e.target.value)} style={{ width: 22, height: 22, padding: 0, border: 'none', borderRadius: 5 }} />
            Colour A
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 'var(--ss-fs-xs)', color: 'var(--ss-text-3)' }}>
            <input type="color" value={genColourB} onChange={(e) => setGenColourB(e.target.value)} style={{ width: 22, height: 22, padding: 0, border: 'none', borderRadius: 5 }} />
            Colour B
          </label>
        </div>
      </div>
      <EdSlider label="Scale" value={genScale} onChange={setGenScale} min={1} max={12} format={(v) => String(v)} showExactValue />
      <EdSlider label="Contrast" value={genContrast} onChange={setGenContrast} min={-100} max={100} showExactValue />
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '0 16px', marginBottom: 10 }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 3, flex: 1, minWidth: 0 }}>
          <span style={{ fontSize: 'var(--ss-fs-3xs)', fontWeight: 600, letterSpacing: '0.1em', color: 'var(--ss-text-3)', textTransform: 'uppercase' }}>Seed</span>
          <input className="ss-input" type="number" value={genSeed}
            onChange={(e) => setGenSeed(parseInt(e.target.value || '0', 10))}
            style={{ padding: '6px 8px', fontSize: 'var(--ss-fs-sm)', borderRadius: 7 }} />
        </label>
        <button className="ss-btn ss-btn--ghost ss-btn--sm" style={{ alignSelf: 'flex-end' }} onClick={() => setGenSeed(Math.floor(Math.random() * 100000))}>Randomize</button>
      </div>
      <div style={{ padding: '0 16px', display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 8 }}>
        <button className="ss-btn ss-btn--primary ss-btn--sm" style={{ justifyContent: 'center' }}
          disabled={genBusy !== null} onClick={() => void generateAndApply(mat.id)}>
          {genBusy === 'texture' ? 'Generating…' : 'Generate as Albedo'}
        </button>
        <button className="ss-btn ss-btn--ghost ss-btn--sm" style={{ justifyContent: 'center' }}
          disabled={genBusy !== null || !mat.albedoMap} title={mat.albedoMap ? undefined : 'Set an Albedo map first'}
          onClick={() => mat.albedoMap && void deriveNormalMap(mat.id, mat.albedoMap)}>
          {genBusy === 'normal' ? 'Deriving…' : 'Derive Normal from Albedo'}
        </button>
      </div>
      {genError && <div style={{ padding: '0 16px 8px', fontSize: 'var(--ss-fs-xs)', color: 'var(--ss-danger)', lineHeight: 1.5 }}>{genError}</div>}
      <M2Label>Paint</M2Label>
      {session.mode !== 'paint' ? (
        <div style={{ padding: '0 16px', marginBottom: 8, fontSize: 'var(--ss-fs-xs)', color: 'var(--ss-text-3)', lineHeight: 1.55 }}>
          Click Paint above the preview to paint directly onto this material's albedo.
        </div>
      ) : (
        <Fragment>
          <EdSlider label="Brush size" value={session.brush.sizePx} onChange={(v) => session.setBrush({ sizePx: v })} min={4} max={120} format={(v) => `${v}px`} showExactValue />
          <EdSlider label="Hardness" value={Math.round(session.brush.hardness * 100)} onChange={(v) => session.setBrush({ hardness: v / 100 })} showExactValue />
          <EdSlider label="Opacity" value={Math.round(session.brush.opacity * 100)} onChange={(v) => session.setBrush({ opacity: v / 100 })} showExactValue />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 6, padding: '0 16px', marginBottom: 8 }}>
            {BRUSH_SWATCHES.map((c) => (
              <button key={c} title={c} onClick={() => session.setBrush({ colourHex: c })} style={{
                aspectRatio: '1', borderRadius: 7, background: c, cursor: 'pointer',
                border: '2px solid ' + (session.brush.colourHex === c ? 'var(--ss-accent)' : 'rgba(255,255,255,0.12)'),
              }} />
            ))}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '0 16px', marginBottom: 10 }}>
            <input type="color" value={session.brush.colourHex} onChange={(e) => session.setBrush({ colourHex: e.target.value })}
              style={{ width: 22, height: 22, padding: 0, border: 'none', borderRadius: 5 }} />
            <span style={{ fontSize: 'var(--ss-fs-xs)', color: 'var(--ss-text-3)' }}>Custom colour</span>
          </div>
          <div style={{ display: 'flex', gap: 6, padding: '0 16px', marginBottom: 8 }}>
            <button className="ss-btn ss-btn--ghost ss-btn--sm" disabled={!session.canUndo} onClick={session.undo}>Undo</button>
            <button className="ss-btn ss-btn--ghost ss-btn--sm" disabled={!session.canRedo} onClick={session.redo}>Redo</button>
            <button className="ss-btn ss-btn--ghost ss-btn--sm" onClick={() => setPendingClear(true)}>Clear</button>
          </div>
          <div style={{ padding: '0 16px', marginBottom: 8 }}>
            <button className="ss-btn ss-btn--primary ss-btn--sm" style={{ justifyContent: 'center', width: '100%' }}
              disabled={paintBusy} onClick={() => void saveAsAlbedo(mat.id)}>
              {paintBusy ? 'Saving…' : 'Save as Albedo'}
            </button>
          </div>
          {paintError && <div style={{ padding: '0 16px 8px', fontSize: 'var(--ss-fs-xs)', color: 'var(--ss-danger)', lineHeight: 1.5 }}>{paintError}</div>}
          <div style={{ padding: '0 16px 8px', fontSize: 'var(--ss-fs-2xs)', color: 'var(--ss-text-3)', lineHeight: 1.55 }}>
            Paints an equirectangular texture using this sphere's own UV layout - a placed object with a different UV unwrap may not line up.
          </div>
        </Fragment>
      )}
      {pendingClear && (
        <EdConfirmDialog
          title="Clear the painted texture?"
          body="Fills the canvas back to this material's flat colour. You can still undo this afterwards."
          confirmLabel="Clear"
          onConfirm={() => { session.clear(mat.colour); setPendingClear(false); }}
          onCancel={() => setPendingClear(false)}
        />
      )}
      <div style={{ padding: '16px 16px 0', fontSize: 'var(--ss-fs-xs)', color: 'var(--ss-text-3)', lineHeight: 1.55 }}>
        Assign materials in the Edit inspector. Shader graphs are on the roadmap.
      </div>
    </M2Panel>
  );
}

/**
 * Particles tab, trimmed out of the old FxRoute and kept as one still
 * full-size legacy panel (docs/editor-consolidation/panel-content-spec.md
 * §9, `fx.particles-legacy`) - it owns a live particle-preview `<Canvas>`,
 * a strangler-fig judgement call the spec leaves for a follow-up
 * decomposition rather than pinning down here. Selection stays a plain
 * local useState (single panel, no siblings to share it with). Reads/writes
 * the same shared `fx` document as the three materials panels above via
 * useSharedModeDocument, not a private useModeDocument call - the document
 * holds both materials and particles in one JSON object, so two independent
 * load-mutate-save cycles on it would silently clobber each other.
 */
export function FxParticlesLegacyRoute() {
  const { data: storedDoc, setData: saveDoc } = useSharedModeDocument<FxDoc>('fx', FX_DOC_FALLBACK);
  const [sel, setSel] = useState('spark_burst');
  const parts = migrateFxDoc(storedDoc).particles;
  const part = parts.find((p) => p.name === sel) ?? parts[0];

  function patchPart(name: string, changes: Partial<FxParticle>) {
    saveDoc((doc) => {
      const migrated = migrateFxDoc(doc);
      return { ...migrated, particles: migrated.particles.map((p) => (p.name === name ? { ...p, ...changes } : p)) };
    });
  }
  function addEntry() {
    const name = `particles_${parts.length + 1}`;
    saveDoc((doc) => {
      const migrated = migrateFxDoc(doc);
      return { ...migrated, particles: [...migrated.particles, { name, preset: 'spark_burst' as const, rate: 40, size: 36, colour: '#71b4fe' }] };
    });
    setSel(name);
  }
  function renameEntry(oldName: string, rawName: string) {
    const nextName = rawName.trim();
    if (!nextName || nextName === oldName) return;
    if (parts.some((p) => p.name === nextName)) return;
    saveDoc((doc) => {
      const migrated = migrateFxDoc(doc);
      return { ...migrated, particles: migrated.particles.map((p) => (p.name === oldName ? { ...p, name: nextName } : p)) };
    });
    setSel(nextName);
  }
  const [pendingDelete, setPendingDelete] = useState<string | null>(null);
  function confirmDeleteEntry() {
    const name = pendingDelete;
    if (!name) return;
    setPendingDelete(null);
    saveDoc((doc) => {
      const migrated = migrateFxDoc(doc);
      return { ...migrated, particles: migrated.particles.filter((p) => p.name !== name) };
    });
    if (sel === name) setSel(parts.find((p) => p.name !== name)?.name ?? '');
  }

  return (
    <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
      <M2Panel side="left" width={244}>
        <M2Label>Particles</M2Label>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, padding: '0 8px' }}>
          {parts.map((p) => (
            <M2ListRow
              key={p.name}
              active={sel === p.name}
              onClick={() => setSel(p.name)}
              onDelete={parts.length > 1 ? () => setPendingDelete(p.name) : undefined}
              deleteLabel={'Delete ' + p.name}
            >
              {p.name}
            </M2ListRow>
          ))}
          <button className="ss-btn ss-btn--ghost" onClick={addEntry} style={{ margin: '8px 4px 0', padding: '5px 0', fontSize: 'var(--ss-fs-xs)', borderRadius: 8, justifyContent: 'center' }}>+ New</button>
        </div>
      </M2Panel>
      <div style={{ position: 'relative', flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'radial-gradient(ellipse 70% 60% at 50% 40%, var(--ss-surface), var(--ss-bg-2) 80%)' }}>
        {part && (
          <div style={{ position: 'relative', width: 360, height: 320 }}>
            <Viewport gl={{ antialias: true }}>
              <PerspectiveCamera makeDefault position={[0, 1.2, 3.2]} fov={45} />
              <color attach="background" args={['#0a0a0c']} />
              <ambientLight intensity={0.5} />
              <ParticleEmitter
                key={`${part.name}-${part.preset}-${part.rate}-${part.size}-${part.colour}`}
                preset={part.preset}
                rate={part.rate}
                size={part.size}
                colour={part.colour}
                seed={7}
              />
              <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -0.01, 0]}>
                <planeGeometry args={[4, 4]} />
                <meshStandardMaterial color="#101216" roughness={0.95} />
              </mesh>
            </Viewport>
          </div>
        )}
        <span className="hm-chip" style={{ position: 'absolute', top: 14, left: 14, fontSize: 'var(--ss-fs-2xs)', padding: '7px 14px' }}>{sel}.particles</span>
      </div>
      <M2Panel side="right" width={252}>
        {part && (
          <Fragment>
            <M2Label>Name</M2Label>
            <div style={{ padding: '0 16px', marginBottom: 8 }}>
              <input className="ss-input" key={part.name} defaultValue={part.name}
                onBlur={(e) => renameEntry(part.name, e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                style={{ padding: '7px 10px', fontSize: 'var(--ss-fs-sm)', fontWeight: 500, borderRadius: 8 }} />
            </div>
            <M2Label>Emitter</M2Label>
            <div style={{ padding: '0 16px', marginBottom: 12 }}>
              <select className="ss-input" value={part.preset} onChange={(e) => patchPart(part.name, { preset: e.target.value as FxEmitterPreset })}
                style={{ appearance: 'none', padding: '8px 11px', fontSize: 'var(--ss-fs-sm)', borderRadius: 9 }}>
                {FX_EMITTER_PRESETS.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <EdSlider label="Rate" value={part.rate} onChange={(v) => patchPart(part.name, { rate: v })} min={1} max={200} format={(v) => String(v)} showExactValue />
            <EdSlider label="Size" value={part.size} onChange={(v) => patchPart(part.name, { size: v })} min={4} format={(v) => v + ' cm'} showExactValue />
            <M2Label>Colour</M2Label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 6, padding: '0 16px' }}>
              {['#71b4fe', '#a8b0d2', '#e8a06c', '#988af6', '#9ed8a8', '#e8806c'].map((c) => (
                <button key={c} title={c} onClick={() => patchPart(part.name, { colour: c })} style={{
                  aspectRatio: '1', borderRadius: 7, background: c, cursor: 'pointer',
                  border: '2px solid ' + (part.colour === c ? 'var(--ss-accent)' : 'rgba(255,255,255,0.12)'),
                }} />
              ))}
            </div>
            <div style={{ padding: '16px 16px 0', fontSize: 'var(--ss-fs-xs)', color: 'var(--ss-text-3)', lineHeight: 1.55 }}>
              Place an FX Emitter from the dock's core pack, then pick a preset on it in the Edit inspector.
            </div>
          </Fragment>
        )}
      </M2Panel>
      {pendingDelete && (
        <EdConfirmDialog
          title={`Delete "${pendingDelete}"?`}
          body="Placed FX Emitter objects set their own preset, rate, size and colour directly and never reference this library by name, so deleting it here has no effect on anything already placed."
          confirmLabel="Delete"
          onConfirm={confirmDeleteEntry}
          onCancel={() => setPendingDelete(null)}
        />
      )}
    </div>
  );
}
